/*---------------------------------------------------------------------------------------------
 *  Copyright (c) by Solusek Systems. All rights reserved.
 *--------------------------------------------------------------------------------------------*/

import { qreq } from "../../shared/qrequest";
import AppActions from "../AppActions";
import BaseCom from "../BaseCom";
import Card from "../Card";
import FormInput from "../FormInput";
import Icon from "../Icon";
import L from "../Lang";
import { Pop } from "../PopModal";
import Switch from "../Switch";
import { withParamsAndNavigate } from "../WithParamsAndNavigate";

class WorkflowEdit extends BaseCom {
    constructor(props) {
        super(props);
        this.state = {
            info: {
                sections: []
            }
        };
    }

    async componentDidMount() {
        await this.loadDep();
        await this.load();
    }

    change = (v) => {
        this.setState({ info: { ...this.state.info } });
    }

    changeFieldValue = (field, name, e) => {
        field[name] = e.target.value;
        this.setState({ info: { ...this.state.info } });
    }


    load = async () => {
        if (this.props.params.id)
            await this.fillItemAsync('/api/workflow/get/' + this.props.params.id, 'info');
    }

    loadDep = async () => {
        await this.fillListAsync('/api/workflow/fieldtype/list', 'fieldTypes');
    }

    addSection = (isIteration) => {
        let info = { ...this.state.info };
        info.sections.push({ isIteration: isIteration });
        this.setState({ info: info });
    }

    removeSection = (section) => {
        Pop.showConfirm(<L>Are you sure you want to remove this section?</L>, <L>Remove Section</L>, () => {
            let info = { ...this.state.info };
            info.sections.splice(info.sections.indexOf(section), 1);
            this.setState({ info: info });
        });
    }

    moveSection = (sec, shiftIdx) => {
        var idx = this.state.info.sections.indexOf(sec);
        let sections = [...this.state.info.sections];
        sections.splice(idx, 1);
        sections.splice(idx + shiftIdx, 0, sec);
        this.setState({ info: { ...this.state.info, sections } });
    }

    addField = (section) => {
        if (!section.fields) section.fields = [];
        section.fields.push({});
        this.setState({ info: { ...this.state.info } });
    }

    removeField = (section, field) => {
        section.fields.splice(section.fields.indexOf(field), 1);
        this.setState({ info: { ...this.state.info } });
    }


    toggleSolidOutput = (section, field) => {
        field.isSolidOutput = !field.isSolidOutput;
        this.setState({ info: { ...this.state.info } });
    }

    addOption = (field) => {
        if (!field.options) field.options = [];
        field.options.push({ value: '' });
        this.setState({ info: { ...this.state.info } });
    }

    removeOption = (field, option) => {
        field.options.splice(field.options.indexOf(option), 1);
        this.setState({ info: { ...this.state.info } });
    }

    setOptionValue = (option, e) => {
        option.value = e.target.value;
        this.setState({ info: { ...this.state.info } });
    }

    setOptionCode = (option, e) => {
        option.code = e.target.value;
        this.setState({ info: { ...this.state.info } });
    }

    setOptionExtra = (option, e) => {
        option.isExtra = e.target.checked;
        this.setState({ info: { ...this.state.info } });
    }

    enumerateSort = (list) => {
        if (!list) return;
        var n = 0;
        list.forEach(i => {
            i.sortOrder = n;
            n++;
        });
    }

    enumerateInfo = (info) => {
        this.enumerateSort(info.sections);
        info.sections.forEach(i => {
            this.enumerateSort(i.fields);
        });
    }

    submit = async (e) => {
        e?.preventDefault();
        this.setState({ submitting: true });
        try {
            let info = { ...this.state.info };

            this.enumerateInfo(info);

            let j = await qreq.postAsync('/api/workflow/save', info);
            if (j.errorCode === 0) {
                this.success('Saved!');
                this.setState({ info: j.item });
                if (!this.props.params.id)
                    this.props.navigate('/workflow/' + j.item.id, { replace: true });
            }
            else {
                this.alert(j.errorMessage);
            }
        }
        catch (e) {
            this.unkownErrorCallback(e);
        }
        this.setState({ submitting: false });
    }

    moveField = (view, field, shiftIdx) => {
        var idx = view.fields.indexOf(field);
        view.fields.splice(idx, 1);
        view.fields.splice(idx + shiftIdx, 0, field);
        this.setState({ info: { ...this.state.info } });
    }

    moveOption = (view, field, option, shiftIdx) => {
        var idx = field.options.indexOf(option);
        field.options.splice(idx, 1);
        field.options.splice(idx + shiftIdx, 0, option);
        this.setState({ info: { ...this.state.info } });
    }

    toggleIsHistorical = (section) => {
        section.isHistorical = !section.isHistorical;
        this.setState({ info: { ...this.state.info } });
    }

    toggleSummaryEdit = (section) => {
        section.isSummaryEdit = !section.isSummaryEdit;
        this.setState({ info: { ...this.state.info } });
    }

    render() {
        return (
            <div className="container-fluid">
                <h1><L>Workflow Edit</L></h1>

                <form onSubmit={this.submit}>

                    <AppActions>
                        <button type="submit" className="btn btn-primary btn-lg">
                            <Icon icon="faSave" /> <L>Save</L></button>
                    </AppActions>

                    <Card title="Workflow Information">
                        <FormInput
                            model={this.state.info}
                            name="name"
                            label="Workflow Name"
                            type="text"
                            onChange={this.change}
                            required
                        />
                        <FormInput
                            model={this.state.info}
                            name="description"
                            label="Description"
                            type="textarea"
                            onChange={this.change}
                        />

                        <FormInput
                            model={this.state.info}
                            name="documentName"
                            label="Document Name"
                            type="text"
                            onChange={this.change}
                            required
                            hint="Title of the rendered document."
                        />


                        <FormInput
                            model={this.state.info}
                            name="isDisabled"
                            label="Disabled"
                            type="switch"
                            onChange={this.change}
                        />
                    </Card>

                    {this.state.info.sections.map((i, idx) => (
                        <Card
                            title={<FormInput
                                model={i}
                                name="name"
                                label="Section Name"
                                type="text"
                                onChange={this.change}
                                labelAsPlaceholder
                                inputGroupClassName="mb-0"
                            />}
                            key={idx}
                            headerButtons={<>
                                <button type="button" className={'btn ' + (i.isSummaryEdit ? 'btn-success' : 'btn-muted')}
                                    title={'Enable summary edit: ' + (i.isSummaryEdit ? 'ON' : 'OFF')}
                                    onClick={() => this.toggleSummaryEdit(i)}>
                                    <Icon icon="faParagraph" />
                                </button>
                                <button type="button" className={'btn ' + (i.isHistorical ? 'btn-success' : 'btn-muted')}
                                    title={'Copy data from previous visit: ' + (i.isHistorical ? 'ON' : 'OFF')}
                                    onClick={() => this.toggleIsHistorical(i)}>
                                    <Icon icon="faMonument" />
                                </button>
                                <span> </span>
                                <button type="button" className="btn btn-secondary" disabled={idx === 0} onClick={() => this.moveSection(i, -1)} title="Move Up">
                                    <Icon icon="faArrowUp" />
                                </button>
                                <button type="button" className="btn btn-secondary" disabled={idx === this.state.info.sections.length - 1} onClick={() => this.moveSection(i, 1)} title="Move Down">
                                    <Icon icon="faArrowDown" />
                                </button>
                                <button type="button" className="btn btn-danger" onClick={() => this.removeSection(i)} title="Remove">
                                    <Icon icon="faTrash" />
                                </button>

                            </>}
                        >


                            {i.fields?.map((j, jdx) => (<div
                                key={idx + '-' + jdx}
                            >
                                <div className="d-flex justify-content-start align-items-center gap-1 mb-1">
                                    <div>
                                        <button type="button" className="btn btn-secondary" disabled={jdx === 0} onClick={() => this.moveField(i, j, -1)}>
                                            <Icon icon="faArrowUp" />
                                        </button>
                                    </div>
                                    <div>
                                        <button type="button" className="btn btn-secondary" disabled={jdx === i.fields.length - 1} onClick={() => this.moveField(i, j, 1)}>
                                            <Icon icon="faArrowDown" />
                                        </button>
                                    </div>
                                    <div className="flex-fill">
                                        {this.state.fieldTypes?.find(x => x.id === parseInt(j.fieldTypeID))?.isVisual ? <textarea
                                            placeholder="Information here..."
                                            onChange={(e) => this.changeFieldValue(j, 'name', e)}
                                            className="form-control"
                                            value={j.name}
                                            required
                                        ></textarea>
                                            : <input
                                                type="text"
                                                placeholder="* Field Name"
                                                onChange={(e) => this.changeFieldValue(j, 'name', e)}
                                                className="form-control"
                                                value={j.name}
                                                required
                                            />}
                                    </div>
                                    <div>
                                        <select
                                            onChange={(e) => this.changeFieldValue(j, 'fieldTypeID', e)}
                                            className="form-control"
                                            value={j.fieldTypeID}
                                            required
                                        >
                                            <option value=""><L>* Type</L></option>
                                            {this.state.fieldTypes.map((f, fdx) => (
                                                <option key={idx + '-' + jdx + '-' + fdx} value={f.id}>{f.name}</option>
                                            ))}
                                        </select>
                                    </div>
                                    {this.state.fieldTypes?.find(x => x.id === parseInt(j.fieldTypeID))?.isFormat && <div>
                                        <input
                                            type="text"
                                            placeholder="__-##-__"
                                            onChange={(e) => this.changeFieldValue(j, 'fieldFormat', e)}
                                            className="form-control"
                                            value={j.fieldFormat}
                                        />
                                    </div>}
                                    <div>
                                        <select className="form-control"
                                            onChange={(e) => this.changeFieldValue(j, 'spacing', e)}>
                                            <option><L>Full</L></option>
                                        </select>
                                    </div>
                                    {this.state.fieldTypes?.find(x => x.id === parseInt(j.fieldTypeID))?.name === 'Multi-Checkbox' && <div>
                                        <button type="button" className={'btn ' + (j.isSolidOutput ? 'btn-success' : 'btn-secondary')}
                                            onClick={() => this.toggleSolidOutput(i, j)} title={j.isSolidOutput ? 'Disable Solid Output' : 'Enable Solid Output'}>
                                            <Icon icon="faEye" />
                                        </button>
                                    </div>}
                                    <div>
                                        <button type="button" className="btn btn-danger rounded" onClick={() => this.removeField(i, j)}>
                                            <Icon icon="faTrash" />
                                        </button>
                                    </div>
                                </div>
                                {this.state.fieldTypes?.find(x => x.id === parseInt(j.fieldTypeID))?.isSelector && <div className="mb-1">
                                    {j.options?.map((o, odx) => (
                                        <div key={odx} className="d-flex mb-1">
                                            <div>
                                                <button type="button" className="btn btn-secondary" disabled={odx === 0} onClick={() => this.moveOption(i, j, o, -1)}>
                                                    <Icon icon="faArrowUp" />
                                                </button>
                                            </div>
                                            <div>
                                                <button type="button" className="btn btn-secondary" disabled={odx === j.options.length - 1} onClick={() => this.moveOption(i, j, o, 1)}>
                                                    <Icon icon="faArrowDown" />
                                                </button>
                                            </div>
                                            <div>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    value={o.value}
                                                    onChange={(e) => this.setOptionValue(o, e)}
                                                    placeholder="Value"
                                                />
                                            </div>
                                            <div>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    value={o.code}
                                                    onChange={(e) => this.setOptionCode(o, e)}
                                                    placeholder="Code"
                                                />
                                            </div>
                                            <div className="d-flex align-items-center">
                                                <div>
                                                    <Switch
                                                        checked={o.isExtra}
                                                        onChange={(e) => this.setOptionExtra(o, e)} />
                                                </div>
                                                {o.isExtra && <div className="text-muted mx-1"><small><L>When selected display text field.</L></small></div>}
                                            </div>
                                            <div>
                                                <button type="button" className="btn btn-danger" onClick={() => this.removeOption(j, o)}>
                                                    <Icon icon="faTrash" />
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                    <button type="button" className="btn btn-primary" onClick={() => this.addOption(j)}>
                                        <Icon icon="faPlus" /> <L>Add Option</L></button>
                                </div>}
                            </div>
                            ))}

                            <div className="text-start">
                                <button type="button" className="btn btn-primary" onClick={() => this.addField(i)}>
                                    <Icon icon="faPlus" /> <L>Add Field</L></button>
                            </div>
                        </Card>
                    ))}

                    <div>
                        <button type="button" className="btn btn-primary" onClick={() => this.addSection(false)}>
                            <Icon icon="faPlus" /> <L>Add Section</L></button>
                        <button type="button" className="btn btn-primary" onClick={() => this.addSection(true)}>
                            <Icon icon="faCirclePlus" /> <L>Add Iteration</L></button>
                    </div>
                </form>
            </div>
        );
    }
}

export default withParamsAndNavigate(WorkflowEdit);